import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';

const MetricCard = ({ icon, label, value, helper }) => (
  <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 1.5, height: '100%' }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Box sx={{ color: 'primary.main' }}>{icon}</Box>
    </Stack>
    <Typography variant="h6" sx={{ fontWeight: 800 }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {helper}
    </Typography>
  </Paper>
);

const formatPercentage = (value) => (typeof value === 'number' ? `${value.toFixed(1)}%` : '-');

const ClassAnalyticsPanel = ({
  analytics,
  students = [],
}) => {
  const topStudents = students.slice(0, 5);
  const bottomStudents = students.slice().reverse().slice(0, 5).reverse();

  return (
    <Stack spacing={1.5}>
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={3}>
          <MetricCard
            icon={<EmojiEventsRoundedIcon fontSize="small" />}
            label="Class Topper"
            value={analytics.classTopper?.studentName || '-'}
            helper={analytics.classTopper ? `${formatPercentage(analytics.classTopper.percentage)} • Rank ${analytics.classTopper.rank || 1}` : 'No results yet'}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            icon={<TrendingUpRoundedIcon fontSize="small" />}
            label="Average Performance"
            value={formatPercentage(analytics.averagePerformance)}
            helper={`${students.length} ranked students`}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            icon={<AssignmentTurnedInRoundedIcon fontSize="small" />}
            label="Pass Percentage"
            value={analytics.passPercentage != null ? `${analytics.passPercentage.toFixed(0)}%` : '-'}
            helper={`${analytics.passCount || 0} passed • ${analytics.failCount || 0} failed`}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            icon={<SchoolRoundedIcon fontSize="small" />}
            label="Students Evaluated"
            value={students.length}
            helper={`${analytics.gradeDistribution?.length || 0} grade bands available`}
          />
        </Grid>
      </Grid>

      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={7}>
          <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 1.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Subject-wise Performance
            </Typography>
            {analytics.subjectChartData?.length ? (
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={analytics.subjectChartData} margin={{ top: 12, right: 10, left: -10, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="subjectName" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                    <ChartTooltip formatter={(value) => [`${value}%`, 'Average']} />
                    <Bar dataKey="averagePercentage" fill="#2563eb" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Alert severity="info">Subject-wise chart will appear when marks are available.</Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 1.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Grade Distribution
            </Typography>
            {analytics.gradeDistribution?.length ? (
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ gap: 1 }}>
                {analytics.gradeDistribution.map((item) => (
                  <Chip
                    key={item.grade}
                    label={`${item.grade}: ${item.count}`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Grade distribution will appear once report card grades are available.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 1.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Top Ranked Students
            </Typography>
            <Stack spacing={1}>
              {topStudents.length ? topStudents.map((student) => (
                <Paper key={`top-${student.studentId}`} variant="outlined" sx={{ p: 1.1, borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {student.studentName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Roll {student.rollNumber || '-'} • {student.classSection}
                      </Typography>
                    </Box>
                    <Stack alignItems="flex-end" spacing={0.2}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {formatPercentage(student.percentage)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Rank {student.rank}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              )) : (
                <Typography variant="body2" color="text.secondary">No ranked students available.</Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 1.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
              Attention Needed
            </Typography>
            <Stack spacing={1}>
              {bottomStudents.length ? bottomStudents.map((student) => (
                <Paper key={`bottom-${student.studentId}`} variant="outlined" sx={{ p: 1.1, borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {student.studentName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Roll {student.rollNumber || '-'} • {student.status}
                      </Typography>
                    </Box>
                    <Stack alignItems="flex-end" spacing={0.2}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {formatPercentage(student.percentage)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Rank {student.rank || '-'}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              )) : (
                <Typography variant="body2" color="text.secondary">No low-performing students to highlight.</Typography>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default ClassAnalyticsPanel;
